/**
 * KIOSK 多語系（中／英／日）。
 *
 * docs/Kiosk_UI_Flow.md 規範三語；zh-TW 為主語言，三語皆完整。
 * 商品名稱等動態內容由 API 的 *_i18n 欄位提供，不在此表。
 */

import { CONTACT_TICKET_STRINGS } from './contact-ticket.i18n';
import { DELIVERY_STRINGS } from './delivery.i18n';
import { ISSUED_TICKET_STRINGS } from './issued-tickets.i18n';
import { PAYMENT_STRINGS } from './payment.i18n';
import { presenceStrings } from './presence/strings';
import { printStrings } from './printing/strings';
import { REFUND_QUOTE_STRINGS } from './refund-quote.i18n';
import { REFUND_SUBMISSION_STRINGS } from './refund-submission.i18n';
import { STANDBY_STRINGS } from './standby.i18n';
import { TICKET_SCAN_STRINGS } from './ticket-scan.i18n';
import { TICKET_SERVICE_PAGES } from './ticket-service-pages.i18n';
import { TICKET_SERVICE_STRINGS } from './ticket-service.i18n';
import { voiceSettingsStrings } from './voice/settings-strings';
export type Locale = 'zh-TW' | 'en' | 'ja';

/** Languages currently available in the kiosk UI; Japanese translations are retained. */
export const LOCALES: { code: Locale; label: string }[] = [
  { code: 'zh-TW', label: '繁中' },
  { code: 'en', label: 'English' },
];

type Dict = Record<string, [zh: string, en: string, ja: string]>;

const STRINGS: Dict = {
  'guest.question': ['請問您是飯店住客嗎？', 'Are you a hotel guest?', 'ホテルにご宿泊ですか？'],
  'guest.hotel': ['住客', 'Hotel guest', '宿泊者'],
  'guest.visitor': ['非住客', 'Non-hotel guest', '宿泊者以外'],
  'guest.required': [
    '請選擇住客或非住客，以取得適用票種與價格。',
    'Choose your guest status to view available tickets and prices.',
    '区分を選択して券種と料金をご確認ください。',
  ],
  ...CONTACT_TICKET_STRINGS,
  ...DELIVERY_STRINGS,
  ...PAYMENT_STRINGS,
  ...REFUND_QUOTE_STRINGS,
  ...REFUND_SUBMISSION_STRINGS,
  ...ISSUED_TICKET_STRINGS,
  ...TICKET_SCAN_STRINGS,
  ...TICKET_SERVICE_PAGES,
  ...TICKET_SERVICE_STRINGS,
  ...printStrings,
  'recommend.childTicketUnavailable': [
    '此行程未提供兒童票，兒童如需同行，請改選成人票並確認總人數。',
    'Child tickets are not available for this itinerary. If children are travelling, please select adult tickets for them and check the total number of travellers.',
    'このプランには子供用チケットがありません。お子様が同行する場合は、大人用チケットを選び、合計人数をご確認ください。',
  ],
  'recommend.checkSelection': [
    '班次或可售狀態已變更，請返回重新選擇。',
    'Availability has changed. Please go back and select again.',
    '空き状況が変わりました。戻って選び直してください。',
  ],
  'recommend.unfilled': ['未填', 'Unset', '未入力'],
  'recommend.checkField': [
    '請依可選範圍確認此欄位。',
    'Please check the allowed range.',
    '選択できる範囲をご確認ください。',
  ],
  'recommend.unspecifiedDuration': [
    '未指定，搜尋 {minutes} 分鐘內行程。',
    'Unspecified: search itineraries within {minutes} minutes.',
    '未指定：{minutes} 分以内のプランを検索します。',
  ],
  'voiceInteraction.greeting': [
    '您好，有什麼需要幫忙的嗎？',
    'Hello! How can I help you?',
    'こんにちは！何かお手伝いしましょうか？',
  ],
  'voiceInteraction.prompt': [
    '您可以直接告訴我想查詢的內容',
    'Tell me what you would like to know',
    '知りたいことをそのままお話しください',
  ],
  'voiceInteraction.listening': [
    '正在聆聽，請說話…',
    'Listening. Please speak…',
    'お話を聞いています…',
  ],
  'voiceInteraction.cancelCapture': ['取消收音', 'Cancel recording', '録音をキャンセル'],
  'voiceInteraction.cancelProcessing': ['取消處理', 'Cancel request', '処理をキャンセル'],
  'voiceInteraction.retry': ['再說一次', 'Speak again', 'もう一度話す'],
  'voiceInteraction.instructions': ['操作說明', 'How to use', '使い方'],
  'voiceInteraction.step1': [
    '點選語音入口即可開始；若尚未收音，請點擊金色麥克風。',
    'Choose voice interaction to begin, or tap the gold microphone when it is ready.',
    '音声案内を選ぶと開始します。待機中は金色のマイクをタップしてください。',
  ],
  'voiceInteraction.step2': [
    '看到「正在聆聽」後，直接說出您的需求，說完稍候即可查看回答。',
    'When you see Listening, say what you need, then pause to see the reply.',
    '聞き取り中の表示が出たら、お話しください。話し終わると回答が表示されます。',
  ],
  'voiceInteraction.step3': [
    '可隨時取消，再點麥克風重試；返回首頁可改用觸控操作。',
    'Cancel at any time and tap the microphone to retry. Return home to use touch controls.',
    'いつでもキャンセルでき、マイクで再試行できます。ホームに戻るとタッチ操作ができます。',
  ],
  'voiceInteraction.result': ['對話內容', 'Conversation', '会話内容'],
  'voiceInteraction.transcribing': [
    '正在辨識您的話語…',
    'Recognizing your speech…',
    'お話を認識しています…',
  ],

  'voiceResult.title': ['語音測試結果', 'Voice test result', '音声テスト結果'],
  'voiceResult.waiting': [
    '正在處理，請稍候...',
    'Processing, please wait...',
    '処理中です。しばらくお待ちください...',
  ],
  'voiceResult.recording': [
    '播放本次錄音（測試）',
    'Play this recording (test)',
    '今回の録音を再生（テスト）',
  ],
  'voiceResult.error.VOICE_SERVICE_UNAVAILABLE': [
    '語音服務暫時無法使用，請稍後再試。',
    'Voice service is temporarily unavailable. Please try again later.',
    '音声サービスを一時的に利用できません。しばらくしてから再度お試しください。',
  ],
  'voiceResult.recordingHint': [
    '這是送給辨識服務的音檔。只暫存本次錄音，重新錄音或離開頁面後清除。',
    'This is the audio sent for transcription. It is cleared when you record again or leave this page.',
    '認識に送信した音声です。再録音またはページ移動で削除されます。',
  ],
  'voiceResult.transcribing': [
    '正在辨識您說的內容…',
    'Recognizing what you said…',
    'お話の内容を認識しています…',
  ],
  'voiceResult.answering': [
    '正在等待 AI 回答…',
    'Waiting for an AI reply…',
    'AI の回答を待っています…',
  ],
  'voiceResult.completed': [
    '辨識與回答完成',
    'Transcription and reply complete',
    '認識と回答が完了しました',
  ],
  'voiceResult.error': [
    '這次語音處理未完成',
    'Voice processing did not complete',
    '音声処理が完了しませんでした',
  ],
  'voiceResult.cancelled': [
    '已取消，可再次點擊麥克風',
    'Cancelled. Tap the microphone to try again',
    'キャンセルしました。マイクで再試行できます',
  ],
  'voiceResult.transcript': ['您說', 'You said', '認識した内容'],
  'voiceResult.answer': ['AI 回答', 'AI reply', 'AI の回答'],
  'voiceResult.timing': ['辨識／回答耗時', 'Transcription / reply time', '認識／回答時間'],
  'voiceResult.error.VOICE_DISABLED': [
    '請先在 CMS 啟用景點文字機器翻譯。',
    'Enable spot translation in CMS first.',
    'CMS で観光情報の翻訳を有効にしてください。',
  ],
  'voiceResult.error.VOICE_KEY_MISSING': [
    'CMS 尚未設定可用的 OpenAI 金鑰。',
    'Configure the OpenAI key in CMS.',
    'CMS の OpenAI キーを設定してください。',
  ],
  'voiceResult.error.AI_SETTINGS_KEY_UNREADABLE': [
    'CMS 金鑰無法解密，請重新設定。',
    'The CMS key cannot be decrypted. Please reset it.',
    'CMS キーを復号できません。再設定してください。',
  ],
  'voiceResult.error.VOICE_BUSY': [
    '語音服務正在處理，請稍後再試。',
    'Voice service is busy. Try again shortly.',
    '音声サービスは処理中です。後でもう一度お試しください。',
  ],
  'voiceResult.error.VOICE_NO_SPEECH': [
    '沒有辨識到語音，請重新說一次。',
    'No speech recognized. Please try again.',
    '音声を認識できませんでした。もう一度お話しください。',
  ],
  'voiceResult.error.VOICE_INVALID_AUDIO': [
    '音訊無效，請重新收音。',
    'Invalid audio. Please record again.',
    '音声が無効です。再録音してください。',
  ],
  'voiceResult.error.VOICE_AUTH_REQUIRED': [
    '機台登入失敗，請聯絡維運人員。',
    'Terminal sign-in failed. Please contact support.',
    '端末の認証に失敗しました。係員にお問い合わせください。',
  ],
  'voiceResult.error.VOICE_ANSWER_FAILED': [
    'AI 回答失敗，辨識文字已保留，請稍後重試。',
    'AI reply failed. Your transcript is preserved. Please try again.',
    'AI の回答に失敗しました。認識した内容は保持されています。',
  ],
  'voiceResult.error.VOICE_CAPTURE_FAILED': [
    '收音失敗，請關閉提示後重試。',
    'Recording failed. Close the message and try again.',
    '録音に失敗しました。メッセージを閉じて再試行してください。',
  ],
  'voiceResult.error.VOICE_WHISPER_UNAVAILABLE': [
    '暫時無法辨識您說的內容，請稍後再試。',
    'We could not recognize your speech. Please try again later.',
    'お話の内容を認識できませんでした。しばらくしてから再度お試しください。',
  ],
  'voiceServices.title': ['今天想查詢什麼？', 'How can we help today?', '何をお調べしますか？'],
  'voiceServices.subtitle': [
    '點選服務，或按下麥克風告訴我',
    'Choose a service, or tap the microphone to speak',
    'サービスを選ぶか、マイクを押してお話しください',
  ],
  'voiceServices.services': ['可查詢的服務', 'Available services', 'サービス一覧'],
  'voiceServices.tickets': ['船票訂購', 'Boat tickets', '乗船券の購入'],
  'voiceServices.schedules': ['班次與票價', 'Times & fares', '時刻・運賃'],
  'voiceServices.water': ['水上活動', 'Water activities', '水上アクティビティ'],
  'voiceServices.packages': ['優惠套票', 'Ticket packages', 'セット券'],
  'voiceServices.recommend': ['行程推薦', 'Trip suggestions', 'おすすめプラン'],
  'voiceServices.spots': ['景點查詢', 'Explore attractions', '観光スポット'],
  'voiceServices.question': [
    '您好，今天想去哪裡玩？',
    'Hello! Where would you like to go?',
    'こんにちは！今日はどこへ行きたいですか？',
  ],
  'voiceServices.example': [
    '可以試著說：「我想查詢明天的船班」',
    'Try saying: “Show me tomorrow’s boat trips”',
    '例：「明日の船の時刻を教えて」',
  ],
  'voiceServices.start': ['點一下開始說話', 'Tap to speak', 'タップして話す'],
  'voiceServices.home': ['返回首頁', 'Home', 'ホームに戻る'],
  'voiceServices.manual': ['手動訂票', 'Book tickets', '手動で購入'],
  'capture.speakHint': [
    '請說話，說完稍停即可',
    'Speak, then pause when you are done',
    'お話しください。話し終わったら少しお待ちください',
  ],
  'capture.preparing': [
    '正在準備，請允許麥克風權限…',
    'Preparing — please allow microphone access…',
    '準備中です。マイクの使用を許可してください…',
  ],
  'capture.listening': [
    '正在收音，請說話…',
    'Listening — please speak…',
    '録音中です。お話しください…',
  ],
  'capture.processing': ['正在處理音訊…', 'Processing audio…', '音声を処理しています…'],
  'capture.completed': ['已完成收音', 'Recording complete', '録音が完了しました'],
  'capture.close': ['關閉', 'Close', '閉じる'],
  'capture.error.permission': [
    '未取得麥克風權限，請允許後再試。',
    'Microphone access denied. Allow access and try again.',
    'マイクの使用を許可してから再度お試しください。',
  ],
  'capture.error.missing': [
    '找不到麥克風，請洽服務人員。',
    'No microphone found. Please contact staff.',
    'マイクが見つかりません。スタッフにお声がけください。',
  ],
  'capture.error.device': [
    '麥克風無法使用，請稍後再試或洽服務人員。',
    'Microphone unavailable. Try again or contact staff.',
    'マイクを使用できません。再度お試しいただくかスタッフにお声がけください。',
  ],
  'capture.error.insecure': [
    '此頁面無法使用麥克風，請洽服務人員。',
    'Microphone access is unavailable on this page. Please contact staff.',
    'このページではマイクを使用できません。スタッフにお声がけください。',
  ],
  'capture.error.assets': [
    '收音元件載入失敗，請稍後再試。',
    'Audio components could not load. Please try again.',
    '録音機能を読み込めませんでした。再度お試しください。',
  ],
  'capture.error.encoding': [
    '音訊處理失敗，請再試一次。',
    'Audio processing failed. Please try again.',
    '音声処理に失敗しました。再度お試しください。',
  ],
  'capture.error.timeout': [
    '準備逾時，請關閉後再試。',
    'Preparation timed out. Close and try again.',
    '準備がタイムアウトしました。閉じて再度お試しください。',
  ],
  'capture.error.silence': [
    '沒有收到語音，請再試一次。',
    'No speech detected. Please try again.',
    '音声を検出できませんでした。再度お試しください。',
  ],
  'capture.error.limit': [
    '收音時間已到，請再說一次較短的內容。',
    'Recording time limit reached. Please try a shorter sentence.',
    '録音時間の上限に達しました。短い内容で再度お試しください。',
  ],
  ...presenceStrings,
  ...STANDBY_STRINGS,
  ...voiceSettingsStrings,
  'common.back': ['返回', 'Back', '戻る'],
  'common.next': ['下一步', 'Next', '次へ'],
  'common.cancel': ['取消', 'Cancel', 'キャンセル'],
  'common.confirm': ['確認', 'Confirm', '確認'],
  'common.callStaff': ['呼叫服務人員', 'Call staff', 'スタッフを呼ぶ'],
  'common.loading': ['載入中…', 'Loading…', '読み込み中…'],
  'common.retry': ['重試', 'Retry', '再試行'],
  'common.restart': ['重新開始', 'Start over', '最初から'],
  'common.home': ['回首頁', 'Home', 'ホーム'],
  'common.errNetwork': [
    '系統暫時無法連線，請稍候重試',
    'Network unavailable, please retry',
    '接続できません。再試行してください',
  ],
  'common.errGeneric': [
    '發生問題，請重試或呼叫服務人員',
    'Something went wrong — retry or call staff',
    '問題が発生しました。スタッフをお呼びください',
  ],

  // ── 頁尾工具列（2026-08-18 設計稿）────────────────────────────────
  'footer.support': ['客服協助', 'Assistance', 'サポート'],
  'footer.supportSub': ['呼叫服務人員', 'Call for staff', 'スタッフを呼ぶ'],
  'footer.supportAcked': ['已通知，請稍候', 'Staff notified', 'お呼びしました'],
  'footer.notice': ['購票須知', 'Before you buy', 'ご購入前に'],
  'footer.noticeSub': ['詳閱購票資訊', 'Read the terms', 'ご案内を読む'],
  'footer.stepLabel': ['購票步驟', 'Purchase step', '購入ステップ'],

  // ── 購票須知彈窗 ──────────────────────────────────────────────────
  // 段落標題與內文都不在這裡：條文是各租戶自己的法律文件，而原本寫死的四段裡
  // 「遊艇航程」「水上設施」是第一家的業務別——沒有那些項目的租戶不該看到。
  // 改由 CMS 的「自助購票機外觀 → 購票須知」維護（見 PurchaseNoticeModal）。
  'notice.title': ['購票須知', 'Before you buy', 'ご購入前のご案内'],
  'notice.pending': ['（尚未提供）', '(Not provided yet)', '（未提供）'],
  'notice.close': ['我知道了', 'Got it', '確認しました'],

  'attract.tagline': [
    '輕觸螢幕 開始選擇你的航程',
    'Touch to start your voyage',
    '画面にタッチして航海を始めましょう',
  ],
  'attract.mascotName': ['小獅船長', 'Captain Lion', 'ライオン船長'],
  'attract.mascotName.noMascot': ['', '', ''],
  'attract.touchStart': ['觸碰開始', 'Touch to start', 'タッチで開始'],
  'attract.touchStartSub': ['瀏覽活動與行程', 'Browse tours & activities', 'アクティビティを見る'],
  // 智慧推薦入口，副標是引導語（後面接省略號）
  'attract.voice': ['智慧推薦', 'Voice', '音声で操作'],
  'attract.voiceSub': ['說出……', 'Just say it…', '話しかけてください…'],
  // 三顆對話泡泡不在這裡：內容是「我想搭船遊○○港」這種自家航線的示範句，
  // 一家一個樣，改由各租戶在機台外觀設定填（attract.bubbles.*，見 Attract.tsx）。
  'attract.todaySchedule': ['今日班次', "Today's sailings", '本日の便'],
  'attract.systemOk': ['系統正常 · 連線正常', 'System OK · Online', 'システム正常 · オンライン'],

  // 品牌名由租戶設定（見 lib/theme.tsx）。沒設定的租戶用 home.welcomeNoBrand ——
  // 這是套版化之後唯一含公司名的文案鍵，其餘 200 個維持在程式內：全部開放覆寫
  // 就變成在做翻譯管理系統，而真正會因公司而異的只有招牌。
  'home.welcome': ['歡迎來到{brand}', 'Welcome to {brand}', '{brand}へようこそ'],
  'home.welcomeNoBrand': ['歡迎光臨', 'Welcome', 'ようこそ'],
  'home.packages': ['精選套票', 'Featured packages', 'おすすめセット券'],
  'home.returnStart': ['返回首頁', 'Return to start', '最初の画面に戻る'],
  'home.subtitle': [
    '請選擇您想體驗的航程或行程',
    'Choose the voyage or experience you want',
    'ご希望の航路・体験をお選びください',
  ],

  // ── 主功能四格（設計稿 p.5，2×2）─────────────────────────────────
  // 名稱與副標一字取自設計稿；此前的「我想搭船／我自己選／我已購票要報到」為舊稱。
  'menu.recommend': ['行程推薦', 'Recommended', 'おすすめ'],
  'menu.recommendSub': ['小獅船長為您安排', 'Captain Lion plans it', '船長がご提案'],
  'menu.recommendSub.noMascot': ['為您安排合適行程', 'We plan it for you', 'おすすめをご提案'],
  'menu.cruise': ['搭船遊港', 'Harbour cruise', 'ハーバークルーズ'],
  'menu.cruiseSub': ['瀏覽所有航程', 'All sailings', 'すべての航路'],
  'menu.water': ['水上設施', 'Water activities', 'ウォーターアクティビティ'],
  'menu.waterSub': ['SUP、水上自行車…', 'SUP, water bikes…', 'SUP・水上自転車など'],
  'menu.ticket': ['查詢票券', 'My ticket', 'チケット照会'],
  'menu.ticketSub': ['我已購票，要報到', 'Already booked — check in', '購入済み・受付へ'],

  'home.recommendTitle': ['我想搭船', 'I want to sail', '船に乗りたい'],
  'home.recommendDesc': [
    '讓小獅船長幫你推薦今天的行程',
    'Let Captain Lion recommend a trip',
    'ライオン船長におすすめを聞く',
  ],
  'home.recommendDesc.noMascot': [
    '為你推薦今天的行程',
    'Get a trip recommendation',
    '本日のおすすめをご案内',
  ],
  'home.browseTitle': ['我自己選', 'Browse myself', '自分で選ぶ'],
  'home.browseDesc': [
    '瀏覽所有遊艇／水上活動',
    'Browse all yachts and activities',
    'すべてのヨット・アクティビティ',
  ],
  'home.checkinTitle': ['我已購票，要報到', 'I have a ticket — check in', 'チケット確認・受付'],
  'home.checkinDesc': ['在這裡查詢票券資訊', 'Look up your ticket here', 'チケット情報を確認'],
  'home.voice': ['用講的', 'Voice', '音声'],

  'recommend.title': [
    '讓小獅船長幫你規劃',
    'Let Captain Lion plan for you',
    'ライオン船長がプラン',
  ],
  'recommend.title.noMascot': ['為你規劃行程', 'Plan your trip', '旅程をプラン'],
  'recommend.timeQ': ['有多少時間？', 'How much time?', 'どのくらい時間が？'],
  'recommend.time30': ['30 分', '30 min', '30分'],
  'recommend.time60': ['1 小時', '1 hr', '1時間'],
  'recommend.time120': ['2 小時', '2 hr', '2時間'],
  'recommend.time240': ['半天', 'Half day', '半日'],
  'recommend.partyQ': ['幾個人？', 'How many people?', '何名様？'],
  'recommend.interestQ': [
    '想要哪種行程？（可複選）',
    'What kind of trip? (multi-select)',
    'どんな旅？（複数選択可）',
  ],
  // 用語對齊 2026-08-19 設計稿的主功能與行程推薦（原本是「航程／體驗」）
  'cat.trip': ['搭船遊港', 'Harbour cruise', 'ハーバークルーズ'],
  'cat.experience': ['水上體驗', 'Water activity', 'ウォーターアクティビティ'],
  'cat.package': ['套裝', 'Package', 'パッケージ'],
  'cat.charter': ['包船', 'Charter', 'チャーター'],
  'recommend.submit': ['幫我推薦', 'Recommend', 'おすすめを見る'],
  // 設計稿的結果頁標題是「專屬行程」，不是「為你推薦」
  'recommend.resultTitle': ['專屬行程', 'Your itinerary', 'あなた専用のプラン'],
  'recommend.book': ['立即訂購', 'Book now', '今すぐ予約'],
  'recommend.empty': [
    '目前沒有符合的行程，換個條件試試',
    'No matching trips — try other options',
    '該当なし。条件を変えてください',
  ],

  'browse.title': ['選擇航程', 'Choose a voyage', '航海を選ぶ'],
  'browse.titleWater': [
    '選擇水上設施',
    'Choose a water activity',
    'ウォーターアクティビティを選ぶ',
  ],
  'browse.empty': ['目前無可售商品', 'No products available', '販売中の商品はありません'],
  'browse.book': ['立即訂購', 'Book now', '今すぐ予約'],
  'browse.minutes': ['分鐘', 'min', '分'],

  'home.spotsDesc': ['看看附近有什麼景點', 'See nearby attractions', '近くの観光スポットを見る'],
  'spots.title': ['景點介紹', 'Attractions', '観光スポット'],
  'spots.empty': [
    '目前無公開景點資料',
    'No published attractions yet',
    '公開された観光スポットはまだありません',
  ],
  'spots.radius': ['觸發範圍', 'Trigger radius', '通知範囲'],
  'spots.coords': ['座標', 'Coordinates', '座標'],

  // ── 行程推薦（設計稿 p.7）──────────────────────────────────────────
  // 設計稿用的是問句：「想安排多久的旅程呢？」而非「停留時間」。
  'recommend.lead': [
    '小獅船長為您規劃專屬行程',
    'Captain Lion plans your trip',
    '船長があなたの旅を計画します',
  ],
  'recommend.lead.noMascot': [
    '為您規劃專屬行程',
    'A trip planned just for you',
    'あなただけの旅程をご提案',
  ],
  'recommend.dateQ': ['選擇體驗日期', 'Choose a date', '体験日を選ぶ'],
  'recommend.durationQ': [
    '想安排多久的旅程呢？',
    'How long do you have?',
    'どのくらいの時間がありますか？',
  ],
  'recommend.partySizeQ': [
    '這次有幾位一起出發呢？',
    'How many are travelling?',
    '何名でお出かけですか？',
  ],
  'recommend.experienceQ': [
    '想體驗哪些行程呢？（可複選）',
    'What would you like? (multi-select)',
    'ご希望の体験は？（複数選択可）',
  ],
  'recommend.ask': ['請小獅船長推薦', 'Ask Captain Lion', '船長におすすめを聞く'],
  'voiceInteraction.prepareFirst': [
    '正在準備，請稍後...',
    'Preparing, please wait...',
    '準備中です。しばらくお待ちください...',
  ],
  'voiceInteraction.readyToSpeak': [
    '請開始說話，正在收音。',
    'Please start speaking. Listening now.',
    'お話しください。録音中です。',
  ],
  'voiceInteraction.querying': [
    '正在查詢推薦…',
    'Looking up recommendations…',
    'おすすめを検索中…',
  ],
  'recommend.summaryParty': [
    '{adults} 大 {children} 小',
    '{adults} adults, {children} children',
    '大人 {adults} 名・子供 {children} 名',
  ],
  'recommend.allTypes': ['不限類型', 'All types', 'すべての種類'],
  'recommend.confirmCriteria': [
    '目前依以下條件查詢：{summary}。',
    'Current search: {summary}.',
    '検索条件：{summary}。',
  ],
  'recommend.editConditions': ['修改條件', 'Edit conditions', '条件を変更'],
  'recommend.closeConditions': ['收起條件', 'Close conditions', '条件を閉じる'],
  'recommend.viewResults': ['查看推薦', 'View recommendations', 'おすすめを見る'],
  'recommend.details': ['查看行程介紹', 'View itinerary details', '行程の詳細を見る'],
  'recommend.finishEditing': [
    '請先送出或收起條件，再繼續說話。',
    'Submit or close the conditions before speaking again.',
    '条件を送信するか閉じてから、もう一度お話しください。',
  ],
  'recommend.ask.noMascot': ['取得推薦', 'Get recommendations', 'おすすめを見る'],
  'recommend.halfDay': ['半天', 'Half day', '半日'],
  'recommend.resultLead': [
    '依照您的選擇，為您安排適合的旅程',
    'Chosen to fit what you told us',
    'ご希望に合わせてご提案します',
  ],
  'recommend.perPerson': ['／人', ' / person', '／名'],
  'recommend.reselect': ['重新選擇', 'Start over', '選び直す'],
  'recommend.orderNow': ['立即訂購', 'Book now', '今すぐ予約'],
  // 兒童年齡區間設計稿標為待確認，先照樣顯示問號，不自行填數字
  'recommend.childRange': ['兒童（?~?歲）', 'Child (?–? yrs)', '子供（?~?歳）'],

  'schedule.title': ['班次與人數', 'Sailing & party size', '便と人数'],
  'schedule.todayLabel': ['今日班次', "Today's sailings", '本日の便'],
  // 清單其實含未來數日，標題不能只說「今日」——一有多天份的班次就會對不上
  'schedule.pickLabel': ['選擇體驗時間', 'Choose your time', '体験時間を選ぶ'],
  'schedule.lead': [
    '選擇出發日期與時段',
    'Pick a date and departure time',
    '出発日と時間帯をお選びください',
  ],
  'schedule.remaining': ['剩餘', 'Left', '残り'],
  'schedule.today': ['今日', 'Today', '本日'],
  'schedule.tomorrow': ['明日', 'Tomorrow', '明日'],
  'schedule.choose': ['選擇', 'Select', '選択'],
  'schedule.selected': ['已選', 'Selected', '選択済み'],
  'schedule.soldOut': ['已售完', 'Sold out', '完売'],
  'schedule.cancelled': ['天候取消', 'Weather cancelled', '天候により欠航'],
  'schedule.salesClosed': ['已停止售票', 'Sales closed', '販売終了'],
  'schedule.partyTitle': ['體驗人數', 'Party size', '体験人数'],
  'schedule.pickFirst': ['請先選擇班次', 'Select a sailing first', '先に便を選択してください'],
  'schedule.adults': ['大人', 'Adults', '大人'],
  'schedule.children': ['兒童', 'Children', '子供'],
  'schedule.subtotal': ['小計', 'Subtotal', '小計'],
  'schedule.originalTotal': ['原價', 'Original', '通常価格'],
  'schedule.promoSaved': ['優惠折抵', 'You save', '割引'],
  'schedule.noSchedule': ['此商品今日無可售班次', 'No sailings today', '本日の便はありません'],
  'schedule.toDelivery': [
    '下一步（取票／發票）',
    'Next (tickets / receipt)',
    '次へ（発券／領収書）',
  ],

  'plan.lead': [
    '請為每一段行程選擇時間',
    'Choose a time for each part of your plan',
    '各体験の時間をお選びください',
  ],
  'plan.segment': ['第 {n} 段', 'Part {n}', '{n} 番目'],
  'plan.changeSlotHint': [
    '如需調整時段，可再次點擊已選班次取消。',
    'To change times, tap a selected departure again to deselect it.',
    '時間を変更するには、選択済みの便をもう一度タップして解除してください。',
  ],
  'plan.pickEach': [
    '請為每一段選擇時間',
    'Choose a time for every part',
    'すべての時間を選択してください',
  ],
  'plan.noSlot': [
    '此段目前沒有可選的時間',
    'No available times for this part',
    'この体験に選べる時間がありません',
  ],
  'plan.clash': ['與已選時段重疊', 'Overlaps a chosen time', '選択済みの時間と重複'],
  'plan.notSellable': [
    '此行程尚未開放購買',
    'This plan is not available yet',
    'このプランは現在ご購入いただけません',
  ],
  'plan.remainingLimit': [
    '本行程最多可購買 {n} 人（受各段名額限制）',
    'Up to {n} people for this plan (limited by each part)',
    'このプランは最大 {n} 名まで（各体験の空き状況による）',
  ],

  'checkout.title': ['取票方式與發票開立', 'Tickets & invoice', '受け取りと領収書'],
  'checkout.lead': [
    '設定完成，領取更方便',
    'Set these up for a smoother pickup',
    '設定しておくと受け取りがスムーズです',
  ],
  'checkout.toPayment': ['下一步（付款方式）', 'Next (payment)', '次へ（お支払い）'],
  'contact.title': ['聯絡資訊', 'Contact details', '連絡先'],
  'contact.note': [
    '請留下聯絡手機；Email 收票或統編發票需填寫 Email',
    'Provide a mobile number; email is required for email tickets or a business invoice',
    '携帯番号をご入力ください。メール受取・会社用請求書にはメールが必要です',
  ],
  'contact.emailPrompt': ['Email', 'Email', 'Email'],
  'contact.emailRequired': ['Email（必填）', 'Email (required)', 'Email（必須）'],
  'contact.emailHint': [
    '請輸入電子信箱，用來接收購票通知或電子發票。',
    'Enter your email address to receive purchase notifications or electronic invoices.',
    '購入通知や電子請求書を受け取るメールアドレスをご入力ください。',
  ],
  // 聯絡資訊上方的說明跟著店家能用的通知渠道換，不承諾做不到的事（2026-10-30）
  'contact.noteBoth': [
    '訂單確認、停航等通知會寄到 Email 或傳簡訊給你',
    'Booking and cancellation notices are sent by email or text',
    '予約確認・欠航などのお知らせはメールまたはSMSで届きます',
  ],
  'contact.noteEmail': [
    '訂單確認、退款與停航通知都會寄到 Email',
    'Booking, refund and cancellation notices go to your email',
    '予約確認・払い戻し・欠航のお知らせはメールで届きます',
  ],
  'contact.noteSms': [
    '訂單確認、停航等通知會傳簡訊給你',
    'Booking and cancellation notices are sent by text',
    '予約確認・欠航などのお知らせはSMSで届きます',
  ],
  // 兩個通知渠道都沒開：講清楚不會有任何通知（含停航），顧客要自己保存票券
  'contact.noNotices': [
    '本店不會另外寄送通知（包括停航），請自行保存票券；出發前可洽櫃台確認航班',
    'This store does not send notices (including cancellations). Please keep your ticket and check with the counter before departure',
    '当店からはお知らせ（欠航を含む）を送信しません。チケットは各自で保管し、出発前にカウンターでご確認ください',
  ],
  'contact.emailPromptOptional': ['Email（選填）', 'Email (optional)', 'Email（任意）'],
  'contact.phonePrompt': ['手機（必填）', 'Mobile (required)', '携帯電話（必須）'],
  'contact.phoneHintSms': [
    '訂單確認與停航等重要異動會傳簡訊到這支手機',
    'We will text this number about your booking and important changes such as cancellations',
    '予約確認や欠航など重要な変更をこの番号にSMSでお知らせします',
  ],
  'contact.phoneHintNoSms': [
    '方便店家必要時聯絡你（不會傳簡訊）',
    'So the store can reach you if needed (no texts are sent)',
    '必要な場合に店舗からご連絡するためです（SMSは送信しません）',
  ],
  'contact.phoneRequired': [
    '請填寫手機號碼',
    'Please enter your mobile number',
    '携帯電話番号を入力してください',
  ],
  'contact.phoneInvalid': [
    '手機號碼格式不正確：台灣手機是 09 開頭 10 碼，國際手機請先選國碼',
    'Invalid mobile number. Taiwan numbers are 10 digits starting with 09; for other countries, pick the country code first',
    '携帯番号の形式が正しくありません。台湾は09で始まる10桁、海外の番号は先に国番号を選んでください',
  ],
  'contact.phoneChecking': [
    '正在確認手機號碼…',
    'Checking your mobile number…',
    '携帯電話番号を確認しています…',
  ],
  'contact.phoneCountryLabel': ['手機國碼', 'Mobile country code', '携帯電話の国番号'],
  'contact.countriesCommon': ['常用', 'Common', 'よく使う国'],
  'contact.countriesOther': ['其他國家', 'Other countries', 'その他の国'],
  // email 必填時說明原因（2026-10-30）：統編、Email 收票、店家只開 email 通知
  'contact.emailRequiredMember': [
    '會員載具需要 Email，用來寄送發票開立通知',
    'An email address is required for member carrier invoice notices',
    '会員用キャリアには発行通知を送るメールが必要です',
  ],
  'contact.emailRequiredCompany': [
    '統編發票需要 Email，用來寄送發票開立通知；或改選其他發票方式',
    'A company invoice needs an email for the invoice notice, or choose another invoice option',
    '統一番号付き領収書には発行通知を送るメールが必要です。または別の発行方法をお選びください',
  ],
  'contact.emailRequiredDelivery': [
    'Email 收票需要 Email，用來寄送取票連結',
    'Receiving tickets by email needs an email address for the ticket link',
    'メールでの受け取りには、チケットのリンクを送るメールが必要です',
  ],
  'contact.emailRequiredStore': [
    '本店的通知只寄 Email，請填寫 Email',
    'This store sends notices by email only. Please enter your email',
    '当店のお知らせはメールのみです。メールアドレスを入力してください',
  ],
  // 建單時後端回 BIZ_CONTACT_REQUIRED：店家剛改了通知設定，帶顧客回來補填
  'contact.settingsChanged': [
    '店家的通知設定剛更新，請確認聯絡資訊後再付款',
    'The store just updated its notice settings. Please check your contact details and try again',
    '店舗のお知らせ設定が更新されました。連絡先をご確認のうえ、もう一度お進みください',
  ],
  'checkout.loading': [
    '正在載入結帳設定…',
    'Loading checkout settings…',
    'お支払い設定を読み込んでいます…',
  ],
  // 缺紙又沒開 email 通知：沒有任何取票方式（首頁與取票頁都顯示）
  'checkout.cannotSell': [
    '目前無法在本機台購票，請洽櫃台',
    'Tickets cannot be purchased at this kiosk right now. Please visit the counter',
    '現在この端末ではチケットを購入できません。カウンターへお越しください',
  ],
  'checkout.needDelivery': [
    '請選擇票券領取方式',
    'Please choose how to receive your ticket',
    'チケットの受け取り方法を選んでください',
  ],
  'checkout.needInvoice': [
    '請選擇發票開立方式',
    'Please choose an invoice option',
    '領収書の発行方法を選んでください',
  ],
  'checkout.carrierInvalid': [
    '請輸入正確的手機條碼（/ 開頭共 8 碼）',
    'Please enter a valid mobile barcode (/ + 7 characters)',
    '正しいモバイルバーコードを入力してください（/＋7桁）',
  ],
  'delivery.title': ['體驗票券領取方式', 'How to receive your ticket', 'チケットの受け取り方法'],
  'delivery.paperOut': [
    '印表機缺紙，紙本 QR 暫不可用；請改用 Email 收票。',
    'Printer out of paper — paper QR unavailable, please use Email.',
    'プリンタ用紙切れ。紙のQRは利用できません。Emailをお選びください。',
  ],
  'delivery.paperQr': ['紙本 QR', 'Paper QR', '紙のQR'],
  'delivery.paperQrDesc': ['現場列印', 'Printed here', 'その場で印刷'],
  'delivery.email': ['Email', 'Email', 'Email'],
  'delivery.online': ['線上取票', 'View tickets online', 'オンライン受け取り'],
  'delivery.onlineDesc': [
    '付款後查看票券、複製連結',
    'View tickets and copy the link after payment',
    '支払い後にチケットを表示・リンクをコピー',
  ],
  'delivery.emailDesc': ['寄到信箱', 'Sent to inbox', 'メールで送信'],
  'delivery.sms': ['簡訊', 'SMS', 'SMS'],
  'delivery.smsDesc': ['寄到手機', 'Sent to your phone', '携帯電話に送信'],
  'delivery.smsUnavailable': ['暫時無法使用', 'Currently unavailable', '現在ご利用いただけません'],
  'delivery.note': [
    '請選擇可用的方式領取票券 QR Code',
    'Choose an available method to receive your ticket QR code',
    '利用可能な方法でチケットのQRコードをお受け取りください',
  ],
  'delivery.emailInvalid': [
    'Email 格式不正確',
    'Invalid email format',
    'Emailの形式が正しくありません',
  ],

  'invoice.title': ['發票開立', 'Invoice', '領収書の発行'],
  'invoice.carrier': ['個人', 'Personal', '個人'],
  'invoice.carrierDesc': ['手機條碼載具', 'Mobile barcode', 'モバイルバーコード'],
  'invoice.memberCarrier': ['Email寄送', 'Member carrier', '会員用キャリア'],
  'invoice.memberCarrierDesc': ['寄到信箱', 'Email delivery', 'メールで送付'],
  'invoice.paper': ['個人', 'Personal', '個人'],
  'invoice.paperDesc': ['一般發票', 'Printed invoice', '紙の領収書'],
  'invoice.donate': ['捐贈', 'Donate', '寄付'],
  'invoice.donateDesc': ['慈善機構', 'To charity', '慈善団体へ'],
  'invoice.donateTo': ['捐贈給 {name}', 'To {name}', '{name}へ寄付'],
  'invoice.donateUnavailable': [
    '暫時無法使用',
    'Temporarily unavailable',
    '現在ご利用いただけません',
  ],
  'invoice.company': ['公司', 'Company', '会社'],
  'invoice.companyDesc': ['統編發票', 'With tax ID', '統一番号'],
  'invoice.carrierPrompt': ['條碼（/開頭 8 碼）', 'Barcode (/ + 7 chars)', 'バーコード（/＋7桁）'],
  'invoice.carrierInvalid': [
    '手機條碼格式不正確：請以 / 開頭，後面輸入 7 碼英文字母、數字或 + - .。',
    'Invalid mobile barcode: enter / followed by 7 letters, digits, or + - .',
    'バーコードの形式が正しくありません。/ の後に英字・数字・+ - . を7文字入力してください。',
  ],
  'invoice.taxIdPrompt': ['統一編號（8 碼）', 'Tax ID (8 digits)', '統一番号（8桁）'],
  'invoice.taxIdInvalid': ['統一編號不正確', 'Invalid tax ID', '統一番号が正しくありません'],
  'invoice.note': ['發票將於付款後開立', 'Invoice issued after payment', '支払い後に発行されます'],

  'payment.title': ['確認金額並完成付款', 'Confirm and pay', '金額の確認とお支払い'],
  'payment.lead': [
    '選擇好付款方式，旅程準備出發！',
    'Pick how to pay — your trip is ready to go!',
    'お支払い方法を選べば出発の準備完了！',
  ],
  'payment.summaryTitle': ['本次體驗', 'Your experience', '今回の体験'],
  'payment.methodsTitle': ['付款方式', 'Payment method', 'お支払い方法'],
  'payment.totalLabel': ['總金額', 'Total', '合計金額'],
  'payment.departs': ['出發', 'departure', '出発'],
  'payment.creditCard': ['信用卡', 'Credit card', 'クレジットカード'],
  'payment.creditCardDesc': ['IC / 磁條 / 感應', 'IC / Swipe / Contactless', 'IC / 磁気 / タッチ'],
  'payment.applePay': ['Apple Pay', 'Apple Pay', 'Apple Pay'],
  'payment.googlePay': ['Google Pay', 'Google Pay', 'Google Pay'],
  'payment.linePay': ['LINE Pay', 'LINE Pay', 'LINE Pay'],
  'payment.note': [
    '選擇後請依刷卡機指示操作',
    'Follow the card reader prompts',
    'カードリーダーの指示に従ってください',
  ],
  'payment.pay': [
    '確認付款（領取票券）',
    'Confirm payment (get tickets)',
    'お支払い確定（チケット受取）',
  ],

  'paying.title': [
    '正在處理付款，請依刷卡機指示',
    'Processing payment — follow the reader',
    '決済処理中 — リーダーの指示に従ってください',
  ],
  'paying.authorizing': ['授權中…', 'Authorizing…', '承認中…'],
  'paying.doNotLeave': [
    '請勿移動或關閉螢幕',
    'Please do not leave or close the screen',
    '画面を閉じないでください',
  ],
  'paying.orderNo': ['訂單號', 'Order no.', '注文番号'],
  'paying.note': [
    '付款成功才會發行票券',
    'Tickets issue only on successful payment',
    '決済成功後にチケット発行',
  ],
  'paying.failedTitle': ['付款未完成', 'Payment not completed', '決済が完了しませんでした'],
  'paying.failedDesc': [
    '未授權成功的訂單不會扣款。',
    'Unauthorized orders are not charged.',
    '未承認の注文に課金はありません。',
  ],
  'paying.oversoldTitle': ['座位剛好售出', 'Seat just sold out', '座席が完売しました'],
  'paying.oversoldDesc': [
    '款項將自動退回原付款方式，無須擔心。',
    'Your payment will be auto-refunded.',
    '自動的に返金されます。',
  ],
  'paying.retry': ['重新付款', 'Pay again', 'もう一度支払う'],

  'done.title': ['購票完成！', 'Booking complete!', '購入完了！'],
  'done.delivering': ['票券正在送達', 'Your ticket is on the way', 'チケットを送信中'],
  'done.paperPrinting': ['紙本 QR Code 印出中…', 'Printing paper QR…', '紙のQRを印刷中…'],
  'done.emailSent': ['票券已寄出 Email', 'Ticket emailed', 'チケットをメール送信'],
  'done.boardingQr': ['登船 QR', 'Boarding QR', '乗船QR'],
  'done.keepQr': [
    '請保留 QR，登船時掃碼',
    'Keep the QR — scan it when boarding',
    'QRを保管し乗船時にスキャン',
  ],
  'done.finish': ['完成', 'Done', '完了'],

  'checkin.title': ['報到 — 請出示 QR Code', 'Check-in — show your QR', '受付 — QRコードを提示'],
  'checkin.guideLine1': [
    '請至登船口由現場人員協助掃碼。',
    'Please proceed to the boarding gate; staff will scan your QR.',
    '乗船口でスタッフがQRを読み取ります。',
  ],
  'checkin.guideLine2': [
    '購票機僅供查詢，實際報到由現場人員完成。',
    'This kiosk is for lookup only; actual check-in is done by staff.',
    'こちらは確認用です。受付は現地スタッフが行います。',
  ],
  'checkin.scanArea': [
    '掃碼區（請至登船口）',
    'Scan area (at boarding gate)',
    'スキャンエリア（乗船口）',
  ],
  'checkin.helpTitle': ['需要幫忙嗎？', 'Need help?', 'お困りですか？'],
  'checkin.help1': ['找不到票券', "Can't find my ticket", 'チケットが見つからない'],
  'checkin.help2': ['QR 掃不到', "QR won't scan", 'QRが読み取れない'],
  'checkin.help3': ['沒收到票', "Didn't receive my ticket", 'チケットが届いていない'],
  'checkin.staffPrompt': [
    '請按「呼叫服務人員」鈴鐺，現場人員會立刻協助。',
    'Press the bell to call staff for immediate help.',
    'ベルを押すとスタッフがすぐに対応します。',
  ],
  'checkin.supervisor': ['督導例外通道', 'Supervisor override', 'スーパーバイザー処理'],

  'supervisor.modalTitle': ['督導例外通道', 'Supervisor override', 'スーパーバイザー処理'],
  'supervisor.warning': [
    '⚠️ 此通道為高責任放行，每筆稽核',
    'High-responsibility override — fully audited',
    '高責任モード — すべて監査対象',
  ],
  'supervisor.reasonLabel': ['原因（必填）', 'Reason (required)', '理由（必須）'],
  'supervisor.reasonPlaceholder': [
    '例：顧客已上船待處理',
    'e.g. passenger boarded pending review',
    '例：乗船済み・要対応',
  ],
  'supervisor.pinLabel': ['主管 PIN', 'Supervisor PIN', 'スーパーバイザーPIN'],
  'supervisor.submit': ['確認放行', 'Confirm override', '承認する'],
  'supervisor.stub': [
    'ⓘ 督導通道後端串接待 D-2 manual_override API。已記錄此次嘗試。',
    'ⓘ Supervisor backend (D-2 manual_override) is pending. Attempt logged.',
    'ⓘ 後端API（D-2）未接続。試行は記録済み。',
  ],

  'voice.title': ['小獅船長能幫你', 'Captain Lion can help', 'ライオン船長がサポート'],
  'voice.title.noMascot': ['我們可以幫你', 'We can help', 'サポートします'],
  'voice.unavailable': [
    'ⓘ 語音對話即將開放，現在可以從常見問題挑一個',
    'ⓘ Voice chat coming soon — pick a common question for now',
    'ⓘ 音声対話は近日公開。よくある質問からお選びください',
  ],
  'voice.q1': ['下一班船幾點？', 'When is the next sailing?', '次の便は何時？'],
  'voice.q2': ['推薦今天的行程', "Recommend today's trip", '本日のおすすめ'],
  'voice.q3': ['我自己選航程', 'Browse all voyages', 'すべての航海'],
  'voice.q4': ['我已購票，要報到', 'I have a ticket — check in', 'チケット確認・受付'],
  'voice.contractNote': [
    'ⓘ 完整語音對話需 C-3 LLM 廠商簽約完成後上線。',
    'ⓘ Full voice conversation activates after C-3 vendor contract.',
    'ⓘ 完全な音声対話はC-3ベンダー契約後に有効化。',
  ],
};

/**
 * 取譯字串。`{name}` 佔位符以 params 內插。缺鍵時回 key 本身（dev 可見）。
 */
/**
 * 這個鍵存不存在。
 *
 * 給「吉祥物關閉時改用 `.noMascot` 版本」用的 —— 只有少數幾句提到角色名，其餘
 * 沒有替代版，問過才知道要不要換（見 session.tsx 的 `t`）。
 */
export function hasString(key: string): boolean {
  return key in STRINGS;
}

export function translate(
  locale: Locale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const entry = STRINGS[key];
  if (!entry) return key;
  const idx = locale === 'zh-TW' ? 0 : locale === 'en' ? 1 : 2;
  let out = entry[idx] || entry[0];
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      out = out.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return out;
}
